import { useState } from 'react';

import { Permission } from '../../../../../../../../../../../../backend/src/auth/permissions/permission';
import {
  DocumentDownloadOptions,
  Fonts,
  TextAlignment,
} from '../../../../../../../../../../../../backend/src/types/types';
import { FONT_FAMILY_OPTIONS, TEXT_ALIGNMENT_OPTIONS } from '@/constants/downloadOptions';
import { cn } from '@/helpers/shadCnUtils';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Switch } from '@/new/components/shadcn/switch';

interface ThreadDownloadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: (options: DocumentDownloadOptions, includeSourceDocuments?: boolean) => void;
  isLoading?: boolean;
}

export function ThreadDownloadDialog({ isOpen, onClose, onDownload, isLoading = false }: ThreadDownloadDialogProps) {
  const [options, setOptions] = useState<DocumentDownloadOptions>({
    fontFamily: Fonts.TimesNewRoman,
    textAlignment: TextAlignment.Left,
    includeReferences: true,
  });
  const [includeSourceDocuments, setIncludeSourceDocuments] = useState(false);

  const handleDownload = () => {
    onDownload(options, includeSourceDocuments);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--download" />
            Download Options
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-6 py-2">
          {/* Font Family Selection */}
          <div className="space-y-2">
            <Label htmlFor="font-family">Font</Label>
            <Select
              value={options.fontFamily}
              onValueChange={(value: Fonts) => setOptions({ ...options, fontFamily: value })}
            >
              <SelectTrigger id="font-family">
                <SelectValue placeholder="Select font" />
              </SelectTrigger>
              <SelectContent>
                {FONT_FAMILY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Text Alignment Selection */}
          <div className="space-y-2">
            <Label htmlFor="text-alignment">Text Alignment</Label>
            <Select
              value={options.textAlignment}
              onValueChange={(value: TextAlignment) => setOptions({ ...options, textAlignment: value })}
            >
              <SelectTrigger id="text-alignment">
                <SelectValue placeholder="Select alignment" />
              </SelectTrigger>
              <SelectContent>
                {TEXT_ALIGNMENT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Include References Toggle */}
          <div className="mt-2 flex items-center gap-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="include-references"
                checked={options.includeReferences}
                onCheckedChange={(checked) => setOptions({ ...options, includeReferences: checked })}
              />
            </div>

            <div className="flex flex-col">
              <Label htmlFor="include-references" className="cursor-pointer">
                Include References
              </Label>
              <span className="text-xs text-gray-500">
                {options.includeReferences
                  ? 'References section will be added at the end'
                  : 'No references will be included'}
              </span>
            </div>
          </div>

          {/* Include Source Documents Toggle */}
          <PermissionGate
            permission={Permission.DocumentDownload}
            uiMode="disabled"
            disabledMessage="You do not have permission to download source documents"
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="include-source-documents"
                  checked={includeSourceDocuments}
                  onCheckedChange={setIncludeSourceDocuments}
                />
              </div>

              <div className="flex flex-col">
                <Label htmlFor="include-source-documents" className="cursor-pointer">
                  Include Source Documents
                </Label>
                <span className="text-xs text-gray-500">
                  {includeSourceDocuments
                    ? 'Answer and all source documents will be packaged as a zip file'
                    : 'Download answer document only'}
                </span>
              </div>
            </div>
          </PermissionGate>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleDownload} disabled={isLoading}>
            {isLoading ? (
              <>
                <Icon className="lucide--loader-circle animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Icon className="lucide--download" />
                Download
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
